import {
    CoffeeIcon,
    Footer,
    Header,
    SUPPORT_URL
} from "./Chrome.js";
import Playground from "./Playground.js";

const PORTFOLIO_URL = "https://diogocrava.dev";
const LINKEDIN_URL = "https://www.linkedin.com/in/diogo-crava/";

interface AppProps {
    baseUrl: string;
}

export default function App({ baseUrl }: AppProps) {
    return (
        <div className="site-shell">
            <a className="skip-link" href="#main">Skip to content</a>

            <Header activePage="home" baseUrl={baseUrl} />

            <main id="main">
                <section className="hero section" id="top">
                    <div className="hero-copy">
                        <div className="hero-kicker">
                            <span className="status-dot" aria-hidden="true" />
                            Open source · MIT licensed
                        </div>
                        <h1>
                            ChronoLaneJS
                            <span> React calendar and scheduler.</span>
                        </h1>
                        <p className="hero-summary">
                            An open-source React and TypeScript calendar and scheduler
                            with accessible views, resource scheduling, event drag and
                            resize interactions, and time-zone-aware behavior.
                        </p>
                        <div className="hero-actions">
                            <a className="button button--primary" href="#playground">
                                Try the playground
                                <span aria-hidden="true">↓</span>
                            </a>
                            <a className="button button--secondary" href={`${baseUrl}docs/`}>
                                Read the docs
                            </a>
                        </div>
                        <div className="install-command" aria-label="Installation command">
                            <span aria-hidden="true">$</span>
                            <code>npm install @chronolanejs/react</code>
                            <span className="command-note">Stable release available</span>
                        </div>
                    </div>

                    <div className="hero-visual" role="img" aria-label="A sample weekly schedule">
                        <div className="hero-visual-glow" />
                        <div className="schedule-card" aria-hidden="true">
                            <div className="schedule-topbar">
                                <div>
                                    <span className="schedule-overline">September 2026</span>
                                    <strong>Product week</strong>
                                </div>
                                <div className="schedule-controls" aria-hidden="true">
                                    <span>‹</span><span>›</span>
                                </div>
                            </div>
                            <div className="schedule-grid">
                                <div className="schedule-time-labels" aria-hidden="true">
                                    <span>09</span><span>11</span><span>13</span><span>15</span>
                                </div>
                                <div className="schedule-day">
                                    <div><strong>MON</strong><span>14</span></div>
                                    <i className="schedule-event event-blue" style={{ top: "18%", height: "22%" }}>
                                        Planning
                                    </i>
                                    <i className="schedule-event event-violet" style={{ top: "48%", height: "18%" }}>
                                        Review
                                    </i>
                                </div>
                                <div className="schedule-day">
                                    <div><strong>TUE</strong><span>15</span></div>
                                    <i className="schedule-event event-cyan" style={{ top: "32%", height: "28%" }}>
                                        Research
                                    </i>
                                </div>
                                <div className="schedule-day">
                                    <div><strong>WED</strong><span>16</span></div>
                                    <i className="schedule-event event-orange" style={{ top: "22%", height: "34%" }}>
                                        Docs
                                    </i>
                                </div>
                                <div className="schedule-day">
                                    <div><strong>THU</strong><span>17</span></div>
                                    <i className="schedule-focus" style={{ top: "55%", height: "25%" }} />
                                </div>
                            </div>
                            <div className="schedule-caption">
                                <span className="schedule-avatar">CL</span>
                                <span><strong>Timezone-safe</strong> across every visible lane</span>
                                <span className="schedule-zone">UTC</span>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="proof-strip" aria-label="Library highlights">
                    <div><strong>5</strong><span>Built-in views</span></div>
                    <div><strong>IANA</strong><span>Timezone aware</span></div>
                    <div><strong>18—19</strong><span>React support</span></div>
                    <div><strong>0</strong><span>State assumptions</span></div>
                </section>

                <Playground baseUrl={baseUrl} />

                <section className="principles section" aria-labelledby="principles-title">
                    <div className="section-heading">
                        <p className="eyebrow">Designed for real products</p>
                        <h2 id="principles-title">Calendar behavior<br />for real products.</h2>
                    </div>
                    <div className="principle-grid">
                        <article>
                            <span>01</span>
                            <h3>Connect your state</h3>
                            <p>
                                Use controlled or uncontrolled navigation and
                                connect selections, edits, and drops to any data layer.
                            </p>
                        </article>
                        <article>
                            <span>02</span>
                            <h3>Customize presentation</h3>
                            <p>
                                Replace meaningful render boundaries while
                                ChronoLaneJS handles layout and calendar behavior.
                            </p>
                        </article>
                        <article>
                            <span>03</span>
                            <h3>Trust the timeline</h3>
                            <p>
                                IANA zones, locale conventions, overlap lanes,
                                clipping, and custom ranges stay explicit and testable.
                            </p>
                        </article>
                    </div>
                </section>

                <section className="docs-cta section" aria-labelledby="docs-cta-title">
                    <div className="docs-cta-copy">
                        <p className="eyebrow">Documentation</p>
                        <h2 id="docs-cta-title">Build on a clear calendar model.</h2>
                        <p>
                            Start with a guided integration, then use the complete
                            API and customization references as the implementation grows.
                        </p>
                    </div>
                    <nav className="docs-cta-links" aria-label="Documentation starting points">
                        <a href={`${baseUrl}docs/getting-started/`}>
                            <span>01</span>
                            <span>
                                <strong>Getting started</strong>
                                <small>Install, model events, and connect application state.</small>
                            </span>
                            <span aria-hidden="true">→</span>
                        </a>
                        <a href={`${baseUrl}docs/api/`}>
                            <span>02</span>
                            <span>
                                <strong>API reference</strong>
                                <small>Components, props, callbacks, renderers, and types.</small>
                            </span>
                            <span aria-hidden="true">→</span>
                        </a>
                        <a href={`${baseUrl}docs/`}>
                            <span>03</span>
                            <span>
                                <strong>Browse all guides</strong>
                                <small>Interactions, resources, time zones, renderers, and accessibility.</small>
                            </span>
                            <span aria-hidden="true">→</span>
                        </a>
                    </nav>
                </section>

                <section className="author-section section" id="author" aria-labelledby="author-title">
                    <div className="author-visual" aria-hidden="true">
                        <span className="author-monogram">DC</span>
                        <div className="author-orbit author-orbit--outer" />
                        <div className="author-orbit author-orbit--inner" />
                        <div className="author-portrait-frame">
                            <img
                                src={`${baseUrl}diogo-crava.png`}
                                alt=""
                                loading="lazy"
                            />
                        </div>
                        <div className="author-terminal">
                            <span aria-hidden="true">&gt;</span>
                            <p>
                                My name is <strong>Diogo Crava</strong>
                                <small>Full-stack developer</small>
                            </p>
                        </div>
                    </div>

                    <div className="author-content">
                        <p className="eyebrow">Behind ChronoLaneJS</p>
                        <h2 id="author-title">
                            Built with intent
                            <span> by Diogo Crava.</span>
                        </h2>
                        <p className="author-lead">
                            I&apos;m a full-stack developer who enjoys turning
                            difficult product behavior into clear, dependable tools.
                            ChronoLaneJS is my take on calendar infrastructure that
                            stays precise, flexible, and pleasant to build with.
                        </p>

                        <nav className="author-links" aria-label="Diogo Crava links">
                            <a href={PORTFOLIO_URL} target="_blank" rel="noreferrer">
                                Portfolio <span aria-hidden="true">↗</span>
                            </a>
                            <a href="https://github.com/MaximilianWalker" target="_blank" rel="noreferrer">
                                GitHub <span aria-hidden="true">↗</span>
                            </a>
                            <a href={LINKEDIN_URL} target="_blank" rel="noreferrer">
                                LinkedIn <span aria-hidden="true">↗</span>
                            </a>
                        </nav>

                        <div className="author-support">
                            <span className="author-support-icon">
                                <CoffeeIcon />
                            </span>
                            <div>
                                <p>Support the work</p>
                                <span>
                                    If ChronoLaneJS saves you time, a coffee helps
                                    fund the next carefully built release.
                                </span>
                            </div>
                            <a href={SUPPORT_URL} target="_blank" rel="noreferrer">
                                Buy me a coffee <span aria-hidden="true">↗</span>
                            </a>
                        </div>
                    </div>
                </section>
            </main>

            <Footer baseUrl={baseUrl} />
        </div>
    );
}
